import React from 'react';
import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { Box, Link, Popper, List, ListItem, Accordion, AccordionSummary, AccordionDetails, Paper, Fade } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as Content from '../dataObjects/contentTypes';

const drawerWidth = '100%';

// will need to take in props including title and description and links
const InfoItemBox = (props: Content.InfoItemBoxProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  
  const handlePopperClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setPopperOpen(true);
  
    const timer = setTimeout(() => {
      setPopperOpen(false);
    }, 3000); // Fade out after 3 seconds
  
    return () => clearTimeout(timer);
  };

  const [expandedAccordions, setExpandedAccordions] = useState<{ [key: string]: boolean }>({});

  const toggleAccordion = (id: string) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getRandomToken = (length: number): string => {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  };

  const renderContentItem = (item: Content.ContentItem, index: number): React.ReactNode => {
    switch (item.type) {
      case 'text':
        return (
          <Typography 
            key={item.type+'-'+index}
            component="span"
            sx={{ 
              display: 'inline',
              whiteSpace: 'normal',
              fontWeight: item.isBold ? 'bold' : 'normal'
            }}
          >
            {item.content}
          </Typography>
        );

      case 'link':
        return (
          <Link 
            href={item.url+'?t=' + getRandomToken(8)}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            underline="hover"
            sx={{ 
              display: 'inline',
              whiteSpace: 'normal',
            }}
          >
            {item.text}
          </Link>
        );

      case 'image':
        return (
          <Link
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-block',
            verticalAlign: 'middle',
            margin: 1,
          }}
        >
          <Box
            component="img"
            src={item.src}
            alt={item.alt}
            sx={{
              maxWidth: '100%',
              width: item.width || 'auto',
              height: item.height || 'auto',
            }}
          />
           </Link>
        );



      default:
        return null;
    }
  };

  const renderBlock = (block: Content.ContentBlock, index: number): React.ReactNode => {
    switch (block.type) {
      case 'paragraph':
        return (
          <Typography key={index} component={'p'} variant="body1" gutterBottom>
            {block.content.map((item, idx) => (
              <React.Fragment key={idx}>{renderContentItem(item, idx)}</React.Fragment>
            ))}
          </Typography>
        );
  
      case 'list':
        const isOrdered = block.listType === 'number';
        return (
          <List
            key={index}
            component={isOrdered ? 'ol' : 'ul'}
            sx={{ listStyleType: isOrdered ? 'decimal' : 'disc', pl: 3 }}
          >
            {block.items.map((item, idx) => (
              <ListItem key={idx} sx={{ display: 'list-item', py: 0 }}>
                {item.content.map((contentItem, contentIdx) => (
                  <React.Fragment key={contentIdx}>{renderContentItem(contentItem, contentIdx)}</React.Fragment>
                ))}
              </ListItem>
            ))}
          </List>
        );
  
      case 'subheading':
        return (
          <Typography 
            component="span"
            key={index}
            sx={{
            display: 'inline',
            whiteSpace: 'normal',
            fontWeight: 'bold'}}>
            {block.content}
          </Typography>
        );
  
      case 'accordion':
        const accordionId = `accordion-${index}`;
        return (
          <Accordion
            key={accordionId}
            expanded={expandedAccordions[accordionId] || false}
            onChange={() => toggleAccordion(accordionId)}
            sx={{
              margin: '8px 0 !important',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                },
              }}
            >
              <Typography component={'span'}>{block.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {block.content.map((nestedBlock, nestedIndex) => renderBlock(nestedBlock, nestedIndex))}
            </AccordionDetails>
          </Accordion>
        );

        
  
      default:
        return null;
    }
  };

  return (
    <Box  sx={{
      maxWidth: drawerWidth,  
      border: 1,               // Sets the border width (1px)
      borderColor: '#bce8f1',   // Sets the border color
      borderRadius: 1,          // Optional: adds rounded corners
      padding: 2,               // Optional: adds padding inside the box
      marginBottom: 2,         // Optional: adds margin below the box
    }}>
    <Typography
      component={'span'}
      variant="h6"
      align="left"
      gutterBottom
      sx={{color: '#0071bc', fontWeight: 'bold'}}
    >
      {props.title}
    </Typography>
  
    {props.subtitle && (
      <Typography
      component={'span'}
        variant="subtitle1"
        color="textSecondary"
        gutterBottom
      >
        {props.subtitle}
      </Typography>
    )}
  
    <Box sx={{ my: 2 }}> {/* margin-y to replace space-y-2 */}
      {props.blocks.map((block, index) => renderBlock(block, index))}
    </Box>
  </Box>
  );
};

export default InfoItemBox;
